// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { z } from 'zod';
import { inputTypeSchema } from './buy-operation';

export const destinationFundSchema = z.object({
  id: z.string(),
  ticker: z.string(),
  name: z.string(),
  isin: z.string().nullable().optional(),
  percentage: z.number().min(0).max(100),
  amount: z.number().min(0),
  shares: z.number().min(0).optional(),
  dfi_url: z.string().nullable().optional(),
});

export type DestinationFundFormData = z.infer<typeof destinationFundSchema>;

export const transferOperationFormSchema = z
  .object({
    type: z.enum(['full', 'partial']),
    percentage: z.number().min(0).max(100),
    shares: z.number().min(0),
    amount: z.number().min(0),
    inputType: inputTypeSchema,
    destinationFunds: z
      .array(destinationFundSchema)
      .min(1, 'At least one destination fund is required'),
  })
  .refine(
    data =>
      data.type === 'full'
        ? data.percentage === 100
        : data.percentage > 0 && data.percentage <= 100,
    { message: 'Invalid refund percentage', path: ['percentage'] },
  )
  .refine(
    data => {
      const total = data.destinationFunds.reduce(
        (sum, fund) => sum + fund.percentage,
        0,
      );
      return Math.abs(total - 100) <= 0.02;
    },
    {
      message: 'Sum of destination fund percentages must equal 100%',
      path: ['destinationFunds'],
    },
  );

export type TransferOperationFormData = z.infer<typeof transferOperationFormSchema>;
